import { doc, setDoc } from "firebase/firestore";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/auth/AuthProvider";
import { ErrorText, FieldLabel, PrimaryButton, SBCard, SecondaryButton } from "@/components/ui";
import { db } from "@/firebase/app";
import {
  isMembershipCurrentlyActive,
  membershipId,
  preferredName,
  USER_ROLE_LABELS,
  type SBotMembership,
} from "@/models/user";
import { deleteAccount, leaveMembership } from "@/services/account";
import "@/features/account/account.css";
import "@/features/home/home.css";
import { useNotificationCenter } from "@/features/notifications/NotificationCenterProvider";

// Port of Views/Tabs/UserView.swift: profile editing (owner-updatable fields
// only), workspace memberships, SBot Labs admin mode toggle, sign out/delete.

export function AccountView() {
  const {
    currentUser,
    isSBotLabsAdmin,
    isSBotAdminModeEnabled,
    setSBotAdminModeEnabled,
    signOut,
    profileMaintenanceWarning,
  } = useAuth();
  const { badgeText } = useNotificationCenter();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [bio, setBio] = useState("");
  const [loadedID, setLoadedID] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (currentUser && currentUser.id !== loadedID) {
      setFirstName(currentUser.firstName);
      setLastName(currentUser.lastName);
      setDisplayName(currentUser.displayName ?? "");
      setBio(currentUser.bio ?? "");
      setLoadedID(currentUser.id);
    }
  }, [currentUser, loadedID]);

  if (!currentUser) return null;
  const activeMemberships = currentUser.memberships.filter((membership) => isMembershipCurrentlyActive(membership));

  const saveProfile = async () => {
    setIsSaving(true);
    setError(null);
    setMessage(null);
    try {
      await setDoc(
        doc(db, "users", currentUser.id),
        {
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          displayName: displayName.trim() || `${firstName.trim()} ${lastName.trim()}`.trim(),
          bio: bio.trim(),
          updatedAt: new Date(),
        },
        { merge: true },
      );
      setMessage("Profile saved.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Save failed.");
    } finally {
      setIsSaving(false);
    }
  };

  const leave = async (id: string) => {
    setError(null);
    try {
      await leaveMembership(id);
      setMessage("Membership removed. It may take a moment to sync.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not leave membership.");
    }
  };

  const removeAccount = async () => {
    setError(null);
    try {
      await deleteAccount();
      await signOut();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Account could not be deleted.");
    }
  };

  return (
    <div className="list-screen">
      <div className="list-screen-head">
        <h1>{preferredName(currentUser)}</h1>
        <span className="badge">{currentUser.email}</span>
      </div>

      {profileMaintenanceWarning && <ErrorText>{profileMaintenanceWarning}</ErrorText>}

      <SBCard className="home-card">
        <h3>Profile</h3>
        <div style={{ display: "flex", gap: 10 }}>
          <FieldLabel label="First name">
            <input value={firstName} onChange={(e) => setFirstName(e.target.value)} />
          </FieldLabel>
          <FieldLabel label="Last name">
            <input value={lastName} onChange={(e) => setLastName(e.target.value)} />
          </FieldLabel>
        </div>
        <FieldLabel label="Display name">
          <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
        </FieldLabel>
        <FieldLabel label="Bio">
          <textarea rows={3} value={bio} onChange={(e) => setBio(e.target.value)} />
        </FieldLabel>
        <PrimaryButton title={isSaving ? "Saving…" : "Save profile"} onClick={() => void saveProfile()} disabled={isSaving} />
        <ErrorText>{error}</ErrorText>
        {message && <p className="home-hint">{message}</p>}
      </SBCard>

      <SBCard className="home-card">
        <details className="account-workspace-management">
          <summary>
            <h3>Workspace Management</h3>
          </summary>
          <div className="account-workspace-memberships">
            {activeMemberships.length === 0 ? (
              <p className="home-hint">No memberships.</p>
            ) : (
              activeMemberships.map((membership) => (
                <div key={membershipId(membership)} className="event-row" style={{ cursor: "default" }}>
                  <div className="event-row-main">
                    <strong>{membership.displayName}</strong>
                    <span>
                      {USER_ROLE_LABELS[membership.role]} · {membership.targetType} · {membership.status}
                    </span>
                  </div>
                  <SecondaryButton
                    title={`Leave ${membership.displayName} - ${membershipRoleLabel(membership)}`}
                    destructive
                    onClick={() => void leave(membershipId(membership))}
                  />
                </div>
              ))
            )}
          </div>
        </details>
      </SBCard>

      <SBCard className="home-card">
        <Link className="event-row" to="/account/notifications">
          <div className="event-row-main">
            <strong>Notifications</strong>
            <span>Requests, replies, and account updates</span>
          </div>
          {badgeText && <span className="badge">{badgeText} unread</span>}
        </Link>
      </SBCard>

      <SBCard className="home-card">
        <Link className="event-row" to="/account/support">
          <div className="event-row-main">
            <strong>Request Support</strong>
            <span>Contact the SBot team and track replies</span>
          </div>
          <span className="badge">open</span>
        </Link>
      </SBCard>

      <SBCard className="home-card">
        <Link className="event-row" to="/account/settings">
          <div className="event-row-main">
            <strong>Settings</strong>
            <span>Multi-window workspace behavior</span>
          </div>
        </Link>
      </SBCard>

      {isSBotLabsAdmin && (
        <SBCard className="home-card">
          <h3>SBot Labs admin</h3>
          <label style={{ display: "flex", gap: 10, alignItems: "center", fontSize: 14 }}>
            <input
              type="checkbox"
              checked={isSBotAdminModeEnabled}
              onChange={(e) => setSBotAdminModeEnabled(e.target.checked)}
              style={{ width: "auto" }}
            />
            Admin mode — swaps the League tab for the SBot Labs Admin tools
          </label>
        </SBCard>
      )}

      <SBCard className="home-card">
        <h3>Session</h3>
        <SecondaryButton title="Sign out" onClick={() => void signOut()} />
        {!confirmDelete ? (
          <SecondaryButton title="Delete account…" destructive onClick={() => setConfirmDelete(true)} />
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <p className="home-hint">
              This permanently deletes your account, profile, and memberships. This cannot be undone.
            </p>
            <div style={{ display: "flex", gap: 10 }}>
              <SecondaryButton title="Cancel" onClick={() => setConfirmDelete(false)} />
              <SecondaryButton title="Permanently delete" destructive onClick={() => void removeAccount()} />
            </div>
          </div>
        )}
      </SBCard>
    </div>
  );
}

function membershipRoleLabel(membership: SBotMembership): string {
  if (membership.role === "admin") {
    return membership.targetType === "league" ? "League Admin" : "Organization Admin";
  }
  return USER_ROLE_LABELS[membership.role];
}
